/**
 * V7.2.A Auto-roll-from-prose (2026-06-02).
 *
 * Detects roll triggers in paragraph text (e.g. "Roll against your Evasion
 * skill at Ease Factor 5" or "Make a Stealth roll") so the SPA can surface
 * a Quick-roll button right on the paragraph card.
 *
 * Survey results from dr_no_adventure.json (7 paragraphs match):
 *   ¶8  Roll against your Evasion skill at the lowest bid…
 *   ¶26 …roll at an Ease Factor of 4…
 *   ¶46 …roll at an Ease Factor of 5…
 *   ¶51 Roll against your Evasion…
 *   ¶56 Make a Stealth roll…
 *   ¶84 …roll at an Ease Factor of 5…
 *   ¶91 Make a Stealth roll…
 *
 * Patterns covered (in priority order):
 *   1. "Roll against your <Skill>"  → skill in capture group
 *   2. "Make a/an <Skill> roll"      → skill in capture group
 *   3. "<Skill> roll"                → skill in capture group (Bond RPG
 *                                       canonical skill names only, so
 *                                       we don't match "drum roll" etc)
 *
 * Ease Factor extraction:
 *   - "Ease Factor (of)? N"  → ef
 *   - "EF N" / "EFN"          → ef
 *   - Default '5' when no EF specifier present (Bond RPG standard default)
 */

/**
 * Canonical Bond RPG skill names. Lowercased for matching the
 * pregen.skills keys produced by character creation. Source: Core
 * Rulebook Chapter 1 skill list (verified against pc_pregens.json).
 */
export const BOND_RPG_SKILLS = [
  'Boating',
  'Charisma',
  'Cryptography',
  'Demolitions',
  'Disguise',
  'Diving',
  'Driving',
  'Electronics',
  'Evasion',
  'Fire Combat',
  'First Aid',
  'Gambling',
  'Hand-to-Hand',
  'Interrogation',
  'Local Customs',
  'Lockpicking',
  'Mountaineering',
  'Perception',
  'Persuasion',
  'Photography',
  'Pickpocket',
  'Piloting',
  'Riding',
  'Riposte',
  'Science',
  'Search',
  'Seduction',
  'Sixth Sense',
  'Stealth',
  'Survival',
  'Torture',
  'Tracking',
] as const;

export type BondRpgSkill = (typeof BOND_RPG_SKILLS)[number];

/**
 * 2026-06-04 dice-roll-feedback fix: the 1983 Victory Games scenario
 * prose uses skill names that don't exactly match the PC pregen sheet
 * keys. The detector recognizes the prose form; SKILL_ALIASES maps each
 * prose skill to the canonical pregen-sheet key used in
 * `seed/canonical/personnel/pc_pregens.json`. ScenarioRunner.executeRoll
 * tries the direct match first, then the alias.
 *
 * Verified differences (from grep of pc_pregens.json):
 *   - prose "Perception"   → pregen "Sixth Sense"
 *   - prose "Hand-to-Hand" → pregen "Hand-to-Hand Combat"
 *   - prose "Lockpicking"  → pregen "Lockpicking and Safecracking"
 *   - prose "Pilot"        → pregen "Piloting"
 *
 * Detector entries with no pregen counterpart (First Aid, Persuasion,
 * Photography, Riposte, Search, Survival, Torture, Tracking) simply
 * don't trigger an available button — that's the existing failure mode
 * and is correct.
 */
export const SKILL_ALIASES: Partial<Record<BondRpgSkill, string>> = {
  Perception: 'Sixth Sense',
  'Hand-to-Hand': 'Hand-to-Hand Combat',
  Lockpicking: 'Lockpicking and Safecracking',
  // 2026-06-08 Fix #C — dr_no ¶43 prose says "Roll for a Persuasion
  // attempt" but no pregen has a "Persuasion" skill key; per Core
  // Rulebook Ch 6, Persuasion rolls use the player's Charisma skill.
  // Without this alias the QuickRollButton on ¶43 silently never
  // rendered (resolveSkillToPregenKey returned null).
  Persuasion: 'Charisma',
};

/**
 * Resolve a detector skill name to the matching key in a pregen's
 * `skills` map. Returns the canonical pregen key (case-preserved) if a
 * direct case-insensitive match exists, otherwise tries SKILL_ALIASES
 * and re-runs the case-insensitive lookup. Returns null if nothing
 * matches — caller decides whether to disable the button or fall back.
 */
export function resolveSkillToPregenKey(
  triggerSkill: string,
  pregenSkillKeys: readonly string[],
): string | null {
  const direct = pregenSkillKeys.find(
    (k) => k.toLowerCase() === triggerSkill.toLowerCase(),
  );
  if (direct !== undefined) return direct;
  const alias = SKILL_ALIASES[triggerSkill as BondRpgSkill];
  if (alias === undefined) return null;
  const viaAlias = pregenSkillKeys.find(
    (k) => k.toLowerCase() === alias.toLowerCase(),
  );
  return viaAlias ?? null;
}

export interface RollTrigger {
  /** Skill name as it appears in the scenario text (TitleCase). */
  skill: BondRpgSkill;
  /** Ease Factor 1-10. Default '5' when not specified in the paragraph. */
  ef: string;
}

/** Build a regex that matches any canonical skill name in a list of alternations. */
const SKILL_ALTERNATION = BOND_RPG_SKILLS.map((s) =>
  s.replace(/-/g, '[-\\s]?'),
).join('|');

const PATTERN_ROLL_AGAINST = new RegExp(
  `\\bRoll(?:ing)?\\s+against\\s+your\\s+(${SKILL_ALTERNATION})\\b`,
  'i',
);
const PATTERN_MAKE_A_ROLL = new RegExp(
  `\\bMake\\s+an?\\s+(${SKILL_ALTERNATION})\\s+roll\\b`,
  'i',
);
const PATTERN_SKILL_ROLL = new RegExp(
  `\\b(${SKILL_ALTERNATION})\\s+roll\\b`,
  'i',
);
// 2026-06-08 Fix #C-bis — dr_no ¶43 prose: "Roll for a Persuasion attempt."
// Pre-fix this matched none of the three patterns above and the QuickRoll
// button never rendered. Match the canonical "Roll for a/an SKILL" form
// with an optional "attempt" / "check" / "roll" suffix.
const PATTERN_ROLL_FOR = new RegExp(
  `\\bRoll(?:ing)?\\s+for\\s+(?:a|an)\\s+(${SKILL_ALTERNATION})(?:\\s+(?:attempt|check|roll))?\\b`,
  'i',
);
// 2026-06-08 Fix #E — bare "<Skill> EF N" pattern. Live MCP playthrough
// on dallas surfaced 30+ instances across all 4 CYOA scenarios that none
// of the four "roll"-anchored patterns catch — e.g. Code Red ¶4 decision
// label "Commandeer a vehicle and pursue (Perception EF 4 to find usable
// wheels…)" or ¶8 "Charisma EF 4 QR 3+ to wrest one away". The EF
// abbreviation paired directly with a canonical Bond RPG skill is itself
// unambiguous signal — no "roll" verb needed. Last-priority so the four
// "roll"-anchored patterns (which carry richer signal) win on overlap.
const PATTERN_SKILL_EF = new RegExp(
  `\\b(${SKILL_ALTERNATION})\\s+EF\\s*\\d{1,2}\\b`,
  'i',
);
const PATTERN_EASE_FACTOR = /Ease\s+Factor\s+(?:of\s+)?(\d{1,2})/i;
const PATTERN_EF_SHORT = /\bEF\s*(\d{1,2})\b/i;

function canonicaliseSkill(rawMatch: string): BondRpgSkill | null {
  const cleaned = rawMatch.trim().toLowerCase().replace(/\s+/g, ' ');
  for (const s of BOND_RPG_SKILLS) {
    if (s.toLowerCase().replace(/\s+/g, ' ') === cleaned) return s;
    if (s.toLowerCase().replace(/-/g, ' ') === cleaned) return s;
  }
  return null;
}

export function detectRollTrigger(text: string): RollTrigger | null {
  if (typeof text !== 'string' || text.length === 0) return null;
  const patterns = [
    PATTERN_ROLL_AGAINST,
    PATTERN_MAKE_A_ROLL,
    PATTERN_ROLL_FOR,
    PATTERN_SKILL_ROLL,
    PATTERN_SKILL_EF,
  ];
  let skill: BondRpgSkill | null = null;
  for (const p of patterns) {
    const m = p.exec(text);
    if (m && m[1]) {
      const c = canonicaliseSkill(m[1]);
      if (c !== null) {
        skill = c;
        break;
      }
    }
  }
  if (skill === null) return null;

  let ef = '5';
  const efMatch = PATTERN_EASE_FACTOR.exec(text) ?? PATTERN_EF_SHORT.exec(text);
  if (efMatch && efMatch[1]) {
    const n = parseInt(efMatch[1], 10);
    if (Number.isFinite(n) && n >= 1 && n <= 10) {
      ef = String(n);
    }
  }
  return { skill, ef };
}

/**
 * 2026-06-07: lightweight "roll mentioned" detector for paragraphs where
 * the prose calls for a roll but no specific skill is named (e.g. "...the
 * agent rolls at an Ease Factor of 5..."). When this returns true but
 * detectRollTrigger returns null, the SPA surfaces "Pick roll" + "Ask
 * GM" buttons on the paragraph so the player isn't left guessing.
 *
 * Conservative: requires both the word "roll" (any form) AND an Ease
 * Factor indicator OR a die specifier — bare "roll" matches like "tank
 * rolls in" must NOT trigger the helper buttons.
 */
export function paragraphMentionsRoll(text: string): boolean {
  if (typeof text !== 'string' || text.length === 0) return false;
  const hasRollVerb = /\broll(?:s|ed|ing)?\b/i.test(text);
  if (!hasRollVerb) return false;
  // 2026-06-08: when detectRollTrigger returns a specific Bond skill +
  // "roll" hit (e.g. "A Perception roll (Base Time 5 min)"), that alone is
  // enough signal to surface the roll-assist buttons — no need to also
  // require an EF/die specifier. Closes Finding #2b from VG35014 MCP
  // playthrough where "A Perception roll" had no EF and no assist row.
  if (detectRollTrigger(text) !== null) return true;
  // EF indicator OR explicit die specifier (d100, 1d100, 2d6 etc.) OR
  // skill-context phrasing ("makes a … roll", "rolls against …").
  const hasEf = PATTERN_EASE_FACTOR.test(text) || PATTERN_EF_SHORT.test(text);
  const hasDie = /\b[1-9]?d(?:6|10|20|100)\b/i.test(text);
  const hasSkillContext =
    /\bmakes?\s+an?\b/i.test(text) || /\brolls?\s+against\b/i.test(text);
  return hasEf || hasDie || hasSkillContext;
}
